import api from './api'
import {
  Produto,
  ProdutoRequest,
  Parceiro,
  PerguntaElegibilidade,
  PerguntaElegibilidadeRequest,
  ResultadoElegibilidade,
  PoliticaParceiro,
  SalvarPoliticaParceiroRequest,
  ParceiroCompativel,
  StatusProduto,
} from '@/types/produto'
import { PageResponse } from '@/types/comum'
import type {
  NovaPoliticaElegibilidadeRequest,
  PoliticaElegibilidade,
  PoliticasElegibilidadeProduto,
} from '@/types/elegibilidade'

const BASE_PRODUTOS = '/api/v1/produtos'
const BASE_PARCEIROS = '/api/v1/parceiros'

export const produtoService = {
  /**
   * `apenasAtivos` (L12): o seletor de produto da reserva pede só os ativos. Sem ele, o
   * back-office vê o catálogo inteiro (tela de cadastro); o consultor recebe só ativos de
   * qualquer forma — o backend força.
   */
  listar: async (
    page: number = 0,
    size: number = 50,
    opcoes: { apenasAtivos?: boolean } = {}
  ): Promise<PageResponse<Produto>> => {
    const ativos = opcoes.apenasAtivos ? '&apenasAtivos=true' : ''
    const response = await api.get<PageResponse<Produto>>(
      `${BASE_PRODUTOS}?page=${page}&size=${size}${ativos}`
    )
    return response as unknown as PageResponse<Produto>
  },

  buscar: async (id: string): Promise<Produto> => {
    const response = await api.get<Produto>(`${BASE_PRODUTOS}/${id}`)
    return response as unknown as Produto
  },

  criar: async (request: ProdutoRequest): Promise<Produto> => {
    const response = await api.post<Produto>(BASE_PRODUTOS, request)
    return response as unknown as Produto
  },

  atualizar: async (id: string, request: ProdutoRequest): Promise<Produto> => {
    const response = await api.put<Produto>(`${BASE_PRODUTOS}/${id}`, request)
    return response as unknown as Produto
  },

  deletar: async (id: string): Promise<void> => {
    await api.delete(`${BASE_PRODUTOS}/${id}`)
  },

  // ─── Onda 5 (AN-11 §2.1 — DEV-S, produtos V11, contrato real do sha 93c5d2f) ───
  // Os dois são só ADMIN no servidor (GERENTE/OPERADOR/CONSULTOR → 403 ACCESS_DENIED).
  // Erros: ApiResponse {success:false, message, errorCode}.

  /**
   * Ativa ou inativa o produto (`PATCH /v1/produtos/{id}/ativo` `{ativo, motivo}`). O servidor
   * audita (`ALTERAR_ATIVO`) e publica `produto-projetado` — é o que faz oportunidades recusar
   * nova reserva de produto inativo (422 `PRODUTO_INATIVO`). Devolve o `ProdutoResponse`
   * completo; mesmo estado → 200 sem efeito. `motivo` (≤500) é obrigatório só ao INATIVAR
   * (422 `MOTIVO_OBRIGATORIO`); 404 `PRODUTO_NAO_ENCONTRADO`; 400 `VALIDATION_ERROR`.
   */
  alterarAtivo: async (id: string, request: AlterarAtivoProdutoRequest): Promise<Produto> => {
    const response = await api.patch<Produto>(`${BASE_PRODUTOS}/${id}/ativo`, request)
    return response as unknown as Produto
  },

  /** Republica a projeção de todos os produtos (`POST /v1/produtos/projecao/ressincronizar`). */
  ressincronizarProjecao: async (): Promise<RessincronizarProjecaoResponse> => {
    const response = await api.post<RessincronizarProjecaoResponse>(
      `${BASE_PRODUTOS}/projecao/ressincronizar`
    )
    return response as unknown as RessincronizarProjecaoResponse
  },

  // ─── P1 — catálogo canônico (AN-PP1 §1.4 e §2.2; DEV-PP1-F1) ───
  // Contrato do desenho; o backend (DEV-PP1-P) é feito em paralelo. Nomes de caminho e de
  // campo concentrados aqui para o ajuste na integração. Erros: ApiResponse
  // {success:false, message, errorCode} — ver `MENSAGENS_ERRO_PRODUTO_P1`.

  /**
   * `GET /v1/produtos/codigo/{codigo}` (CONSULTOR+). Consultor recebe 404
   * `PRODUTO_NAO_ENCONTRADO` para produto RETIRADA ou inativo; back-office vê qualquer status.
   */
  obterPorCodigo: async (codigo: string): Promise<Produto> => {
    const response = await api.get<Produto>(`${BASE_PRODUTOS}/codigo/${encodeURIComponent(codigo)}`)
    return response as unknown as Produto
  },

  /**
   * `PATCH /v1/produtos/{id}/status` `{status, motivo, confirmacao?}` — só ADMIN (demais 403).
   * Motivo sempre obrigatório (422 `MOTIVO_OBRIGATORIO`); para `RETIRADA` exige
   * `confirmacao: "RETIRAR"` (422 `CONFIRMACAO_OBRIGATORIA`); `RETIRADA` é terminal (422
   * `PRODUTO_RETIRADO`); `OTHER_NEED` tem status fixo (422 `STATUS_FIXO_DO_PRODUTO`). Mesmo
   * status → 200 sem efeito. Devolve o `ProdutoResponse` completo (retirar também desliga `ativo`).
   */
  alterarStatus: async (id: string, request: AlterarStatusProdutoRequest): Promise<Produto> => {
    const response = await api.patch<Produto>(`${BASE_PRODUTOS}/${id}/status`, request)
    return response as unknown as Produto
  },
}

// ─── P1 — tipos do catálogo canônico (ajustar aqui na integração) ───
// Constantes e mensagens de erro ficam em `constants/catalogoProdutoP1` (os testes mocam este
// módulo inteiro; constante aqui sumiria nos testes das telas).

/** Corpo de `PATCH /v1/produtos/{id}/status` (P1). */
export interface AlterarStatusProdutoRequest {
  status: StatusProduto
  /** Sempre obrigatório (≤500). */
  motivo: string
  /** Só para `RETIRADA`: precisa ser exatamente `CONFIRMACAO_RETIRADA` ("RETIRAR"). */
  confirmacao?: string
}

/** Corpo de `PATCH /v1/produtos/{id}/ativo` (Onda 5). */
export interface AlterarAtivoProdutoRequest {
  ativo: boolean
  /** Obrigatório (≤500) só ao inativar; ao ativar pode ir nulo. */
  motivo?: string | null
}

/** Resposta de `POST /v1/produtos/projecao/ressincronizar` (Onda 5). */
export interface RessincronizarProjecaoResponse {
  produtosRepublicados: number
}

export const parceiroService = {
  // O backend (GET /parceiros) devolve uma List<ParceiroResponse> simples,
  // não uma página — por isso retornamos um array direto.
  listar: async (): Promise<Parceiro[]> => {
    const response = await api.get<Parceiro[]>(BASE_PARCEIROS)
    return (response as unknown as Parceiro[]) ?? []
  },

  buscar: async (id: string): Promise<Parceiro> => {
    const response = await api.get<Parceiro>(`${BASE_PARCEIROS}/${id}`)
    return response as unknown as Parceiro
  },
}

// ─── Elegibilidade por produto (Etapa 7) ───

export const elegibilidadeService = {
  /** Perguntas do produto. Por padrão só as ativas — que é o que o consultor responde. */
  perguntas: async (
    produtoId: string,
    apenasAtivas = true
  ): Promise<PerguntaElegibilidade[]> => {
    return await api.get<PerguntaElegibilidade[]>(
      `${BASE_PRODUTOS}/${produtoId}/perguntas-elegibilidade?apenasAtivas=${apenasAtivas}`
    )
  },

  /**
   * Verifica a elegibilidade de uma operação. Devolve o motivo separado em bloqueios,
   * alertas e pendências — não um "não" sem explicação.
   */
  avaliar: async (
    produtoId: string,
    entrada: {
      valorOperacao?: number | null
      prazoMeses?: number | null
      respostas: Record<string, string>
      /** Faturamento anual da empresa (etapa 1) — é a base da regra desde 07/09. */
      faturamentoAnual?: number | null
      /** Só para produto com LTV (Home Equity): crédito solicitado e valor do imóvel. */
      valorCreditoSolicitado?: number | null
      valorImovel?: number | null
    }
  ): Promise<ResultadoElegibilidade> => {
    return await api.post<ResultadoElegibilidade>(
      `${BASE_PRODUTOS}/${produtoId}/elegibilidade`,
      {
        valorOperacao: entrada.valorOperacao ?? null,
        prazoMeses: entrada.prazoMeses ?? null,
        respostas: entrada.respostas,
        faturamentoAnual: entrada.faturamentoAnual ?? null,
        valorCreditoSolicitado: entrada.valorCreditoSolicitado ?? null,
        valorImovel: entrada.valorImovel ?? null,
      }
    )
  },

  criarPergunta: async (
    produtoId: string,
    request: PerguntaElegibilidadeRequest
  ): Promise<PerguntaElegibilidade> => {
    return await api.post<PerguntaElegibilidade>(
      `${BASE_PRODUTOS}/${produtoId}/perguntas-elegibilidade`,
      request
    )
  },

  // Aninhados sob o produto de propósito: a pergunta não existe fora dele, e o gateway
  // roteia por prefixo — um caminho de primeiro nível exigiria rota nova só para isto.
  atualizarPergunta: async (
    produtoId: string,
    perguntaId: string,
    request: PerguntaElegibilidadeRequest
  ): Promise<PerguntaElegibilidade> => {
    return await api.put<PerguntaElegibilidade>(
      `${BASE_PRODUTOS}/${produtoId}/perguntas-elegibilidade/${perguntaId}`,
      request
    )
  },

  removerPergunta: async (produtoId: string, perguntaId: string): Promise<void> => {
    await api.delete(`${BASE_PRODUTOS}/${produtoId}/perguntas-elegibilidade/${perguntaId}`)
  },
}

// ─── Política de elegibilidade do parceiro ───

export const politicaParceiroService = {
  /** Políticas já iniciadas do parceiro — uma por produto que ele opera. */
  doParceiro: async (parceiroId: string): Promise<PoliticaParceiro[]> => {
    return await api.get<PoliticaParceiro[]>(`${BASE_PARCEIROS}/${parceiroId}/politicas`)
  },

  /**
   * Formulário da política para um produto. Vem completo mesmo sem respostas — critério
   * ausente da lista seria indistinguível de critério sem valor.
   */
  obter: async (parceiroId: string, produtoId: string): Promise<PoliticaParceiro> => {
    return await api.get<PoliticaParceiro>(
      `${BASE_PARCEIROS}/${parceiroId}/politicas/${produtoId}`
    )
  },

  salvar: async (
    parceiroId: string,
    produtoId: string,
    request: SalvarPoliticaParceiroRequest
  ): Promise<PoliticaParceiro> => {
    return await api.put<PoliticaParceiro>(
      `${BASE_PARCEIROS}/${parceiroId}/politicas/${produtoId}`,
      request
    )
  },
}

/**
 * Motor de compatibilidade: quais parceiros servem para uma operação. Filtro com motivo,
 * não ranking — todos os parceiros voltam, os incompatíveis marcados.
 */
// ─── P2 — política de elegibilidade versionada por produto (AN-PP2 §2.3; DEV-PP2-F) ───
// Fonte única da regra (produtos V14); oportunidades e simulação recebem por evento. Formas
// conferidas contra o `PoliticaElegibilidadeController` real na integração.

export const politicaElegibilidadeService = {
  /**
   * `GET /v1/produtos/{id}/politicas-elegibilidade` (CONSULTOR+). Consultor recebe só a vigente;
   * back-office, o histórico completo. Resposta `{produtoId, produtoCodigo, vigente?, historico[]}`;
   * `historico` reordenado por versão desc (ordem da tela).
   */
  listar: async (produtoId: string): Promise<PoliticasElegibilidadeProduto> => {
    const r = (await api.get(`${BASE_PRODUTOS}/${produtoId}/politicas-elegibilidade`)) as unknown as
      PoliticasElegibilidadeProduto
    return { ...r, historico: [...(r?.historico ?? [])].sort((a, b) => b.versao - a.versao) }
  },

  /**
   * `POST /v1/produtos/{id}/politicas-elegibilidade` — só ADMIN. P6 (AN-PP6 §3.4): PROPÕE a versão —
   * 202 com a proposta `PENDENTE_APROVACAO` (não muda a regra vigente nem publica evento); 200 sem
   * efeito quando a regra é igual à aberta (devolve a ATIVA) ou à pendente (reenvio; devolve a mesma
   * pendente). 409 `PROPOSTA_JA_PENDENTE`. 422: `MOTIVO_OBRIGATORIO`, `ORIGEM_OBRIGATORIA`,
   * `TIPO_OBRIGATORIO`, `PARAMETROS_INCOMPATIVEIS_COM_TIPO`, `TIPO_FIXO_DO_PRODUTO`, `PRODUTO_RETIRADO`,
   * `VIGENCIA_RETROATIVA`, `VIGENCIA_CONFLITANTE`, `AREA_DONA_INVALIDA`.
   */
  criarVersao: async (
    produtoId: string,
    request: NovaPoliticaElegibilidadeRequest
  ): Promise<PoliticaElegibilidade> =>
    (await api.post(`${BASE_PRODUTOS}/${produtoId}/politicas-elegibilidade`, request)) as unknown as PoliticaElegibilidade,

  /**
   * `POST …/{politicaId}/aprovar` (GERENTE+, outra pessoa). PENDENTE → ATIVA; encerra a aberta na
   * véspera do início da nova. 403 `APROVACAO_PELO_PROPONENTE`/`APROVADOR_SEM_PAPEL`; 409
   * `TRANSICAO_PUBLICACAO_INVALIDA`, `VIGENCIA_EXPIRADA_NA_APROVACAO`, `VIGENCIA_CONFLITANTE`, `CONFLITO_VERSAO`.
   */
  aprovar: async (produtoId: string, politicaId: string, versaoRegistro?: number | null): Promise<PoliticaElegibilidade> =>
    (await api.post(
      `${BASE_PRODUTOS}/${produtoId}/politicas-elegibilidade/${politicaId}/aprovar`,
      versaoRegistro != null ? { versaoRegistro } : {}
    )) as unknown as PoliticaElegibilidade,

  /** `POST …/{politicaId}/rejeitar` (GERENTE+). 422 `MOTIVO_OBRIGATORIO`; 409 `TRANSICAO_PUBLICACAO_INVALIDA`, `CONFLITO_VERSAO`. */
  rejeitar: async (
    produtoId: string,
    politicaId: string,
    motivo: string,
    versaoRegistro?: number | null
  ): Promise<PoliticaElegibilidade> =>
    (await api.post(`${BASE_PRODUTOS}/${produtoId}/politicas-elegibilidade/${politicaId}/rejeitar`, {
      motivo,
      ...(versaoRegistro != null ? { versaoRegistro } : {}),
    })) as unknown as PoliticaElegibilidade,
}

export const compatibilidadeParceiroService = {
  avaliar: async (
    produtoId: string,
    atributos: Record<string, string>
  ): Promise<ParceiroCompativel[]> => {
    return await api.post<ParceiroCompativel[]>(
      `${BASE_PRODUTOS}/${produtoId}/parceiros-compativeis`,
      { atributos }
    )
  },
}
